import type { ToolUIPart, UIMessage } from 'ai';

import type { ElementOf } from 'types/utilityTypes';

export type AiCompanionMode = 'sidebar' | 'fullscreen';

export const subjectEntityTypes = [
  'Goal',
  'KeyResult',
  'Initiative',
  'StrategicElement',
  'KPI',
  'Team',
  'Nudge',
] as const;
export type SubjectEntityType = ElementOf<typeof subjectEntityTypes>;

export type SubjectEntity = {
  id: number;
  type: SubjectEntityType;
};

/**
 * An entity and a prompt are independent: name what the conversation is about,
 * what to ask, or both. One of the two is required, and an entity is only ever
 * passed complete - the Companion rejects half of one.
 *
 * Prompt text is composed by the frontend, from `prompts.ts`, so it can carry
 * what the page knows (the selected cycle, the scope).
 */
export type SubjectArgs =
  | { entity: SubjectEntity; prompt?: string }
  | { entity?: SubjectEntity; prompt: string };

export type SetSubjectFn = (args: SubjectArgs) => void;

// Public API for external consumers (NavBar, AppLayout, etc.)
export type AiCompanionSidebar = {
  isOpen: boolean;
  toggle: () => void;
  close: () => void;
  mode: AiCompanionMode;
  setMode: (mode: AiCompanionMode) => void;
  toggleMode: () => void;
  setSubject: SetSubjectFn;
  sidebarWidth: number;
};

export type CompanionProfile = {
  disclaimer: string;
};

export type ThreadMeta = {
  id: string;
  name: string | null;
  createdAt: string | null;
};

export type StarterSpec = { label: string; message: string };

export type SubjectText = { text: string };

export type ThinkingStep = {
  id: string;
  label: string;
  state: ToolUIPart['state'];
};

export type ActionSpec = { label: string; payload: string };

export type CompanionWorkspace = {
  chatId: string;
  initialMessages: UIMessage[];
  isHistoryOpen: boolean;
  openThread: (threadId: ThreadMeta['id']) => void;
  refreshThreads: () => void;
  renameThread: (threadId: ThreadMeta['id'], name: string) => void;
  deleteThread: (threadId: ThreadMeta['id']) => void;
  starters: StarterSpec[];
  startNewChat: () => void;
  threads: ThreadMeta[];
  threadsError: boolean;
  threadsLoading: boolean;
  toggleHistory: () => void;
};

// Internal context with additional properties for aiCompanion components
export type AiCompanionSidebarContextType = AiCompanionSidebar & {
  currentUrl: string;
  setSidebarWidth: (width: number) => void;
  persistSidebarWidth: (width: number) => void;
  pendingSubject: SubjectArgs | null;
  consumeSubject: () => void;
};
